import { CalendarIcon, CircleAlert, Pencil } from "lucide-react"
import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Calendar } from "@/components/ui/calendar"
import { format } from "date-fns"
import { es } from "date-fns/locale"
import { updateLeaveRequest } from "@/lib/collections/requestsLeave/user/updateLeaveRequest"
import { isFullDayLeave } from "@/requests/leaves/lib/isFullDayLeave"
import type { LeaveRequest } from "@/lib/collections/requestsLeave/user/getCertificateRequestsByUser "

interface EditRequestLeaveProps {
    request: LeaveRequest;
    onSuccess?: () => void;
}

const EditRequestLeave = ({ request, onSuccess }: EditRequestLeaveProps) => {
    // Permisos antiguos de varios días (8:00-17:00) se abren como "Día completo"
    const initialAllDay = isFullDayLeave(request);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [generalError, setGeneralError] = useState("");
    const [fieldErrors, setFieldErrors] = useState<{
        title?: string;
        dateStart?: string;
        startTime?: string;
        endDate?: string;
        endTime?: string;
        description?: string;
    }>({});

    // Form state — se inicializa con los datos del request
    const [title, setTitle] = useState(request.title);
    const [description, setDescription] = useState(request.comment || "");
    const [allDay, setAllDay] = useState(initialAllDay);
    const [dateStart, setStartDate] = useState<Date | undefined>(new Date(request.startDate));
    const [endDate, setEndDate] = useState<Date | undefined>(new Date(request.endDate));
    const [startTime, setStartTime] = useState(
        !initialAllDay ? format(new Date(request.startDate), "HH:mm") : ""
    );
    const [endTime, setEndTime] = useState(
        !initialAllDay ? format(new Date(request.endDate), "HH:mm") : ""
    );
    const [openStartDate, setOpenStartDate] = useState(false);
    const [openEndDate, setOpenEndDate] = useState(false);

    // Reset cuando cambia allDay
    useEffect(() => {
        setStartDate(undefined);
        setEndDate(undefined);
        setStartTime("");
        setEndTime("");
    }, [allDay]);

    // Reset form cuando se abre el dialog
    useEffect(() => {
        if (isDialogOpen) {
            setTitle(request.title);
            setDescription(request.comment || "");
            setAllDay(initialAllDay);
            setStartDate(new Date(request.startDate));
            setEndDate(new Date(request.endDate));
            setStartTime(!initialAllDay ? format(new Date(request.startDate), "HH:mm") : "");
            setEndTime(!initialAllDay ? format(new Date(request.endDate), "HH:mm") : "");
            setGeneralError("");
            setFieldErrors({});
        }
    }, [isDialogOpen]);

    const combineDateAndTime = (date: Date, time: string): Date => {
        const [hours, minutes] = time.split(":").map(Number);
        const combined = new Date(date);
        combined.setHours(hours, minutes, 0, 0);
        return combined;
    };

    const clearErrors = () => {
        setGeneralError("");
        setFieldErrors({});
    };

    const handleSave = async () => {
        if (isSubmitting) return;
        clearErrors();

        const errors: typeof fieldErrors = {};
        if (!title.trim()) errors.title = "El título es obligatorio";
        if (!dateStart) errors.dateStart = "La fecha de inicio es obligatoria";
        if (!description.trim()) errors.description = "El comentario es obligatorio";
        if (!allDay) {
            if (!startTime) errors.startTime = "La hora de inicio es obligatoria";
            if (!endTime) errors.endTime = "La hora final es obligatoria";
        }

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }

        const ecuadorNow = new Date();
        const startDateOnly = new Date(dateStart!.getFullYear(), dateStart!.getMonth(), dateStart!.getDate());
        const nowDateOnly = new Date(ecuadorNow.getFullYear(), ecuadorNow.getMonth(), ecuadorNow.getDate());

        if (startDateOnly < nowDateOnly) {
            setFieldErrors({ dateStart: "No puedes solicitar permisos para fechas pasadas" });
            return;
        }

        if (allDay && startDateOnly.getTime() === nowDateOnly.getTime()) {
            if (ecuadorNow.getHours() >= 8) {
                setFieldErrors({ dateStart: "No puedes solicitar todo el día porque la jornada laboral ya comenzó (8:00 AM)" });
                return;
            }
        }

        if (allDay && endDate) {
            const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
            if (endDateOnly < startDateOnly) {
                setFieldErrors({ endDate: "La fecha final no puede ser anterior a la fecha inicial" });
                return;
            }
        }

        if (!allDay && startTime && endTime) {
            const finalStart = combineDateAndTime(dateStart!, startTime);
            const finalEnd = combineDateAndTime(dateStart!, endTime);

            if (finalEnd <= finalStart) {
                setFieldErrors({ endTime: "La hora final debe ser posterior a la hora de inicio" });
                return;
            }

            if (startDateOnly.getTime() === nowDateOnly.getTime()) {
                const currentTimeStr = ecuadorNow.toLocaleTimeString('es-EC', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                });
                if (finalStart <= ecuadorNow) {
                    setFieldErrors({ startTime: `La hora de inicio debe ser posterior a la hora actual (${currentTimeStr})` });
                    return;
                }
            }
        }

        setIsSubmitting(true);
        const loadingToast = toast.loading("Actualizando solicitud...");

        try {
            let finalStartDate: Date;
            let finalEndDate: Date;

            if (allDay) {
                finalStartDate = new Date(dateStart!);
                finalStartDate.setHours(8, 0, 0, 0);
                finalEndDate = new Date(endDate ?? dateStart!);
                finalEndDate.setHours(17, 0, 0, 0);
            } else {
                finalStartDate = combineDateAndTime(dateStart!, startTime);
                finalEndDate = combineDateAndTime(dateStart!, endTime);
            }

            const result = await updateLeaveRequest({
                requestId: request.$id,
                title: title.trim(),
                startDate: finalStartDate,
                endDate: finalEndDate,
                allDay,
                comment: description.trim(),
            });

            toast.dismiss(loadingToast);

            if (result.success) {
                toast.success("Solicitud actualizada correctamente");
                setIsDialogOpen(false);
                onSuccess?.();
            } else {
                setGeneralError(result.error || "Error al actualizar");
            }
        } catch (error: any) {
            toast.dismiss(loadingToast);
            setGeneralError(error?.message || "Error inesperado");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="cursor-pointer gap-1">
                    <Pencil className="w-3 h-3" />
                    Editar
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Editar solicitud de permiso</DialogTitle>
                    <DialogDescription>
                        Al editar, la solicitud volverá a estado pendiente para revisión.
                    </DialogDescription>
                    {generalError && (
                        <div className="flex items-center gap-2 rounded-md border border-red-300 bg-red-50 dark:bg-red-950 dark:border-red-800 px-3 py-2 text-sm text-red-600 dark:text-red-400">
                            <CircleAlert className="h-4 w-4 shrink-0" />
                            <span>{generalError}</span>
                        </div>
                    )}
                </DialogHeader>

                <div className="grid gap-3">
                    <Label>Título</Label>
                    <Input
                        value={title}
                        onChange={(e) => {
                            setTitle(e.target.value);
                            if (fieldErrors.title) setFieldErrors(p => ({ ...p, title: undefined }));
                        }}
                        className={fieldErrors.title ? "border-red-500" : ""}
                        maxLength={100}
                    />
                    {fieldErrors.title && <p className="text-xs text-red-500">{fieldErrors.title}</p>}
                </div>

                <div className="grid gap-4">
                    <div className="flex items-center space-x-2">
                        <Switch id="allDay" checked={allDay} onCheckedChange={setAllDay} />
                        <Label htmlFor="allDay">¿Día completo?</Label>
                    </div>

                    <div className="grid gap-3">
                        <Label>{allDay ? "Desde" : "Fecha de inicio"}</Label>
                        <Popover open={openStartDate} onOpenChange={setOpenStartDate}>
                            <PopoverTrigger asChild>
                                <Button variant="outline" className={`w-full justify-start text-left font-normal ${fieldErrors.dateStart ? "border-red-500" : ""}`}>
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {dateStart ? format(dateStart, "PPP", { locale: es }) : "Selecciona una fecha"}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                    mode="single"
                                    selected={dateStart}
                                    captionLayout="dropdown"
                                    disabled={(date) => {
                                        const day = date.getDay();
                                        return day === 0 || day === 6 || date < new Date(new Date().setHours(0, 0, 0, 0));
                                    }}
                                    onSelect={(date) => {
                                        setStartDate(date);
                                        if (allDay && date && (!endDate || endDate < date)) setEndDate(date);
                                        setOpenStartDate(false);
                                        if (fieldErrors.dateStart) setFieldErrors(p => ({ ...p, dateStart: undefined }));
                                    }}
                                />
                            </PopoverContent>
                        </Popover>
                        {fieldErrors.dateStart && <p className="text-xs text-red-500">{fieldErrors.dateStart}</p>}

                        {!allDay && (
                            <Input
                                type="time"
                                value={startTime}
                                onChange={(e) => {
                                    setStartTime(e.target.value);
                                    if (fieldErrors.startTime) setFieldErrors(p => ({ ...p, startTime: undefined }));
                                }}
                                className={`[&::-webkit-calendar-picker-indicator]:brightness-0 dark:[&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:hover:opacity-100 [&::-webkit-calendar-picker-indicator]:cursor-pointer${fieldErrors.startTime ? " border-red-500" : ""}`}
                            />
                        )}
                        {fieldErrors.startTime && <p className="text-xs text-red-500">{fieldErrors.startTime}</p>}
                        {allDay && <p className="text-xs text-gray-500">Se registrará de 8:00 AM a 5:00 PM cada día laborable.</p>}
                    </div>

                    {allDay && (
                    <div className="grid gap-3">
                            <Label>Hasta</Label>
                            <Popover open={openEndDate} onOpenChange={setOpenEndDate}>
                                <PopoverTrigger asChild>
                                    <Button variant="outline" className={`w-full justify-start text-left font-normal ${fieldErrors.endDate ? "border-red-500" : ""}`}>
                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                        {endDate ? format(endDate, "PPP", { locale: es }) : "Selecciona una fecha"}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                    <Calendar
                                        mode="single"
                                        selected={endDate}
                                        captionLayout="dropdown"
                                        disabled={(date) => {
                                            const day = date.getDay();
                                            const isBeforeStart = !!dateStart && date < new Date(new Date(dateStart).setHours(0, 0, 0, 0));
                                            return day === 0 || day === 6 || date < new Date(new Date().setHours(0, 0, 0, 0)) || isBeforeStart;
                                        }}
                                        onSelect={(date) => {
                                            setEndDate(date);
                                            setOpenEndDate(false);
                                            if (fieldErrors.endDate) setFieldErrors(p => ({ ...p, endDate: undefined }));
                                        }}
                                    />
                                </PopoverContent>
                            </Popover>
                            {fieldErrors.endDate && <p className="text-xs text-red-500">{fieldErrors.endDate}</p>}
                    </div>
                    )}

                    {!allDay && (
                        <div className="grid gap-3">
                            <Label>Hora de fin</Label>
                            <Input
                                type="time"
                                value={endTime}
                                onChange={(e) => {
                                    setEndTime(e.target.value);
                                    if (fieldErrors.endTime) setFieldErrors(p => ({ ...p, endTime: undefined }));
                                }}
                                className={`[&::-webkit-calendar-picker-indicator]:brightness-0 dark:[&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:hover:opacity-100 [&::-webkit-calendar-picker-indicator]:cursor-pointer${fieldErrors.endTime ? " border-red-500" : ""}`}
                            />
                            {fieldErrors.endTime && <p className="text-xs text-red-500">{fieldErrors.endTime}</p>}
                        </div>
                    )}

                    <div className="grid gap-3">
                        <Label>Comentario</Label>
                        <Textarea
                            value={description}
                            onChange={(e) => {
                                setDescription(e.target.value);
                                if (fieldErrors.description) setFieldErrors(p => ({ ...p, description: undefined }));
                            }}
                            className={fieldErrors.description ? "border-red-500" : ""}
                            placeholder="Describe el motivo de tu solicitud..."
                            maxLength={500}
                        />
                        {fieldErrors.description && <p className="text-xs text-red-500">{fieldErrors.description}</p>}
                        <span className="text-xs text-gray-500">{description.length}/500 caracteres</span>
                    </div>
                </div>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" disabled={isSubmitting} className="cursor-pointer">Cancelar</Button>
                    </DialogClose>
                    <Button onClick={handleSave} disabled={isSubmitting} className="cursor-pointer">
                        {isSubmitting ? "Guardando..." : "Guardar cambios"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default EditRequestLeave;